"use client";

// Sprint 3 Fix #3 — offline detection banner.
// Closes Audit Journey Step 7: pre-fix the platform was silent when the
// browser went offline; a restaurant employee on shaky branch wifi would
// have no idea their progress wasn't being saved.
//
// Behaviour:
//   - listens to window 'online' / 'offline' events + initial navigator.onLine
//   - shows a sticky banner at the top of the viewport when offline
//   - briefly shows a "back online" confirmation for 4s once reconnected
//   - bilingual via dictionary (no hardcoded strings)
import { useEffect, useState } from "react";

import type { Dictionary } from "@/lib/i18n";

type State = "online" | "offline" | "reconnected";

type OfflineBannerProps = {
  dictionary: Dictionary;
};

export function OfflineBanner({ dictionary }: OfflineBannerProps) {
  const [state, setState] = useState<State>("online");

  useEffect(() => {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      setState("offline");
    }

    const handleOffline = () => setState("offline");
    const handleOnline = () => {
      setState("reconnected");
      const timer = window.setTimeout(() => setState("online"), 4000);
      return () => window.clearTimeout(timer);
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  if (state === "online") return null;

  const isOffline = state === "offline";
  return (
    <div
      role="status"
      aria-live="polite"
      className="offline-banner"
      data-state={state}
    >
      {isOffline ? dictionary.common.offlineBanner : dictionary.common.onlineBanner}
    </div>
  );
}
